import type { JfPredictionPayload, JfSoilLayer } from "@tbm/shared";
import { buildSoilColourMap, soilColour } from "./soilColours";

interface Props {
  payload: JfPredictionPayload | null;
  /** Drive setup soils — used to drive per-layer rows. */
  setupSoils: JfSoilLayer[];
}

/**
 * Per-layer view of the saved soil schedule with the model coefficients
 * the prediction is currently using. One row per soil layer, ordered by
 * chainage. When the prediction has run, coefficients come from the
 * blended live-NN output (`payload.soilSummary` keyed by description);
 * before that the user-initial values are shown so the panel is never
 * empty after Save.
 */
export function SoilSummaryPanel({ payload, setupSoils }: Props) {
  if (setupSoils.length === 0) return null;

  const summaryByDesc = new Map(
    (payload?.soilSummary ?? []).map((r) => [r.soilType, r]),
  );

  // Build the colour map off the chainage-sorted setup soils so the
  // first-occurrence assignment matches the order the operator sees
  // in the table below.
  const colourMap = buildSoilColourMap(
    [...setupSoils]
      .sort((a, b) => a.startM - b.startM)
      .map((s) => ({ soilType: s.soilType })),
  );

  const rows = [...setupSoils]
    .sort((a, b) => a.startM - b.startM)
    .map((layer) => {
      const summary = summaryByDesc.get(layer.soilType);
      return {
        soilType: layer.soilType,
        startM: layer.startM,
        endM: layer.endM,
        sfSsKpa: summary?.sfSsKpa ?? layer.sfSsKpaInit,
        c: summary?.c ?? layer.cInit,
        a: summary?.a ?? layer.aInit,
        blend: summary?.blend ?? 0,
        exposureM: summary?.exposureM ?? 0,
      };
    });

  return (
    <div className="card overflow-x-auto">
      {/* Fixed table layout + 1fr columns gives every column the same
          width regardless of content length, which stops the soil
          description from squashing the numeric columns. */}
      <table className="w-full text-[11.5px] table-fixed">
        <thead className="bg-surface2 text-[10.5px] text-dim">
          <tr>
            <Th>Soil</Th>
            <Th>Start (m)</Th>
            <Th>End (m)</Th>
            <Th>Skin friction (kPa)</Th>
            <Th>Constant c</Th>
            <Th>Constant a</Th>
            <Th>Encountered (m)</Th>
            <Th>Blend</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border1">
              <Td>
                <span className="inline-flex items-center gap-2 truncate">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: soilColour(r.soilType, colourMap) }}
                  />
                  <span className="truncate">{r.soilType}</span>
                </span>
              </Td>
              <Td numeric>{r.startM.toFixed(1)}</Td>
              <Td numeric>{r.endM.toFixed(1)}</Td>
              <Td numeric>{r.sfSsKpa.toFixed(2)}</Td>
              <Td numeric>{r.c.toFixed(2)}</Td>
              <Td numeric>{r.a.toFixed(2)}</Td>
              <Td numeric>{r.exposureM.toFixed(1)}</Td>
              <Td numeric>{Math.round(r.blend * 100)}%</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="text-left font-medium px-3 py-2 truncate">{children}</th>
  );
}

function Td({
  children,
  numeric = false,
}: {
  children?: React.ReactNode;
  numeric?: boolean;
}) {
  return (
    <td className={`text-left px-3 py-1.5 ${numeric ? "tabular-nums" : ""}`}>
      {children}
    </td>
  );
}
